/**
 * @file StatementTable.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-24
 * @modified 2026-10-20
 *
 * 1.3.0: the approval badge says who set it and when (a run can be shared).
 * 1.4.0: the "Xero check" column (StatementIntegrityBadge — opens the
 *        statement's check via onIntegrityClick), a per-row class
 *        (getRowClassName — a mismatch row is red), and actions that are
 *        disabled with a reason (isDisabled + disabledTitle) or busy (isBusy).
 * 1.5.0: onRowClick / selectedId — Generate Statements' Review selects a row
 *        to preview it beside the table.
 * 1.5.1: the actions sit in an inner .actions-cell div (the td is no longer
 *        made a flex box).
 * 2.0.0 (2026-10-20, Cam — the statements look): a selection tick leads each
 *        row (selectedIds / onToggleSelect / onToggleSelectAll — Review's bulk
 *        bar); whether a customer's invoices are bundled is a toggle in the
 *        Invoices cell (the separate tick column is gone); the approval badge
 *        carries the bare status class the shared CSS styles
 *        (`approval-badge approved`, it rendered `approval-approved`, which
 *        matched nothing) and says "To review" / "Approved" / "Unapproved"
 *        with an icon; compact 40px rows (`sr-table`).
 * 2.1.0 (2026-10-20, Cam — statement notes): an "Office note" column (the
 *        office's own note: icon + the text, cut short, the whole note on
 *        hover) and, beside the customer's name, an "On statement" badge when
 *        there is a note for the customer (printed on the statement and in the
 *        email) — so the two are told apart at a glance.
 */

import './DataTable.css';
import PaginationControls from './PaginationControls';
import { NOTE_FIELDS, hasOfficeNote, hasCustomerNote, lastChangedText } from '../utils/statementNotes';
import StatementIntegrityBadge from './StatementIntegrityBadge';
import TickCheckbox from './TickCheckbox';

const APPROVAL = {
  approved: { label: 'Approved', icon: 'check' },
  unapproved: { label: 'Unapproved', icon: 'block' },
  unchecked: { label: 'To review', icon: null }
};

function StatementTable({
  statements = [],
  columns = ['all'],
  actions = [],
  sortField = 'account_no',
  sortDirection = 'asc',
  onSort,
  pagination,
  onPageChange,
  loading = false,
  emptyMessage = 'No statements found',
  // Whether each customer's invoices go with the statement (the Invoices cell).
  includeInvoiceIds,
  onToggleInclude,
  // The "Xero check" column
  getRowClassName,
  onIntegrityClick,
  integrityAvailable = true,
  integrityReason = null,
  integrityPeriodEndLabel,
  // Generate Statements' Review: a row click selects the statement (its
  // preview shows beside the table); the selected row is highlighted.
  onRowClick,
  selectedId = null,
  // Ticked rows, for the bulk bar.
  selectedIds,
  onToggleSelect,
  onToggleSelectAll
}) {
  const showSelect = !!(selectedIds && onToggleSelect);
  const allTicked = showSelect && statements.length > 0 && statements.every(s => selectedIds.has(s.id));
  const canBundle = !!(includeInvoiceIds && onToggleInclude);

  const availableColumns = {
    accountNo: { key: 'accountNo', label: 'Acc #', sortable: true },
    company: { key: 'account', label: 'Name', sortable: true },
    email: { key: 'email', label: 'Email', sortable: true },
    invoiceCount: { key: 'invoiceCount', label: 'Invoices', sortable: true, align: 'center', format: 'invoices' },
    total: { key: 'total', label: 'Total', sortable: true, format: 'currency', align: 'right' },
    payments: { key: 'payments', label: 'Payments', sortable: true, format: 'currency', align: 'right' },
    balance: { key: 'balance', label: 'Balance', sortable: true, format: 'currency', align: 'right' },
    approval: { key: 'approval_status', label: 'Approval', sortable: true, format: 'approval' },
    officeNote: { key: 'office_note', label: 'Office note', format: 'officeNote' },
    // Not sortable: the approval table is always ordered with mismatches first.
    integrity: { key: 'integrity', label: 'Xero check', format: 'integrity', align: 'center' }
  };

  const visibleColumns = columns === 'all'
    ? Object.values(availableColumns)
    : columns.map(col => availableColumns[col]).filter(Boolean);

  // "Approved by Jade Ward, Mon 12 Oct, 14:05" — a run can be shared, so who did it matters.
  const approvalTitle = (statement) => {
    if (!statement?.approved_by_name || !statement?.approved_at) return undefined;
    const d = new Date(statement.approved_at);
    const when = Number.isNaN(d.getTime()) ? '' : `, ${d.toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
    return `${statement.approval_status === 'unapproved' ? 'Unapproved' : 'Approved'} by ${statement.approved_by_name}${when}`;
  };

  const invoicesCell = (statement) => {
    const n = statement.invoiceCount ?? 0;
    if (!canBundle) return String(n);
    // None in the month: a plain count, nothing to bundle.
    if (!n) return <span className="sr-bundle-none" title="No invoices in the month">0</span>;
    const on = includeInvoiceIds.has(statement.id);
    const title = on
      ? 'Invoices are bundled with this statement — click to send the statement only'
      : 'Statement only — click to bundle this customer’s invoices with it';
    return (
      <button
        type="button"
        className={`sr-bundle${on ? ' on' : ''}`}
        aria-pressed={on}
        aria-label={`Invoices for ${statement.account || 'this customer'}: ${n}, ${on ? 'bundled' : 'not bundled'}`}
        title={title}
        onClick={(e) => { e.stopPropagation(); onToggleInclude(statement.id); }}
      >
        <span className="material-symbols-rounded">{on ? 'receipt_long' : 'do_not_disturb_on'}</span>
        {n}
      </button>
    );
  };

  // The office's own note: only seen here (never on the statement or in the email).
  const officeNoteCell = (statement) => {
    if (!hasOfficeNote(statement)) return null;
    const by = lastChangedText(statement.office_note_updated_by_name, statement.office_note_updated_at);
    return (
      <span className="sr-office-note" title={`Office note (only seen here): ${String(statement.office_note).trim()}${by ? `\n${by}` : ''}`}>
        <span className="material-symbols-rounded" aria-hidden="true">{NOTE_FIELDS.office_note.icon}</span>
        <span className="sr-office-note-text">{String(statement.office_note).trim()}</span>
      </span>
    );
  };

  const formatValue = (value, format, statement) => {
    // Special handling for numeric fields where 0 is valid
    if (format === 'currency' || format === 'number') {
        if (value === null || value === undefined) return '-';
    } else if (format !== 'approval' && format !== 'invoices' && format !== 'officeNote') {
        if (value === null || value === undefined || value === '') return '-';
    }

    switch(format) {
      case 'currency':
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 2
        }).format(value || 0);
      case 'approval': {
        const status = APPROVAL[value] ? value : 'unchecked';
        const a = APPROVAL[status];
        return (
          <span className={`approval-badge ${status}`} title={status !== 'unchecked' ? approvalTitle(statement) : undefined}>
            {a.icon && <span className="material-symbols-rounded" aria-hidden="true">{a.icon}</span>}
            {a.label}
          </span>
        );
      }
      case 'invoices':
        return invoicesCell(statement);
      case 'officeNote':
        return officeNoteCell(statement);
      case 'number':
        return value.toString();
      default:
        return value || '-';
    }
  };

  const handleSort = (column) => {
    if (!column.sortable || !onSort) return;
    onSort(column.key);
  };

  return (
    <>
      {pagination && pagination.total > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange}
        />
      )}
      <div className="data-table-container">
        {loading ? (
          <div className="table-loading">Loading statements...</div>
        ) : statements.length === 0 ? (
          <div className="table-empty">{emptyMessage}</div>
        ) : (
          <table className="data-table sr-table">
            <thead>
              <tr>
                {showSelect && (
                  <th className="sel-col">
                    <TickCheckbox
                      id="stmt-select-all"
                      checked={allTicked}
                      onChange={() => onToggleSelectAll?.(!allTicked)}
                      ariaLabel={allTicked ? 'Untick every statement shown' : 'Tick every statement shown'}
                    />
                  </th>
                )}
                {visibleColumns.map(column => (
                  <th
                    key={column.key}
                    className={`${column.align || ''} ${column.sortable ? 'sortable' : ''}`}
                    onClick={() => handleSort(column)}
                    title={column.format === 'invoices' && canBundle ? 'Invoices in the month — click a count to bundle them with the statement or leave them off' : undefined}
                  >
                    {column.label} {column.sortable && sortField === column.key && (sortDirection === 'asc' ? '▲' : '▼')}
                  </th>
                ))}
                {actions.length > 0 && <th className="actions-col" aria-label="Actions"></th>}
              </tr>
            </thead>
            <tbody>
              {statements.map(statement => {
                const ticked = showSelect && selectedIds.has(statement.id);
                return (
                <tr
                  key={statement.id}
                  data-statement-id={statement.id}
                  className={[getRowClassName?.(statement), onRowClick ? 'clickable-row' : '', selectedId === statement.id ? 'selected-row' : '', ticked ? 'ticked-row' : ''].filter(Boolean).join(' ') || undefined}
                  aria-selected={onRowClick ? selectedId === statement.id : undefined}
                  onClick={onRowClick ? (e) => {
                    // A button, tick or link in the row does its own thing.
                    if (e.target.closest('button, input, label, a')) return;
                    onRowClick(statement);
                  } : undefined}
                >
                  {showSelect && (
                    <td className="sel-col">
                      <TickCheckbox
                        id={`stmt-select-${statement.id}`}
                        checked={ticked}
                        onChange={() => onToggleSelect(statement.id)}
                        ariaLabel={`Tick ${statement.account || 'this statement'}`}
                      />
                    </td>
                  )}
                  {visibleColumns.map(column => (
                    <td
                      key={column.key}
                      className={`col-${column.key}${column.align ? ` ${column.align}` : ''}`}
                    >
                      {column.format === 'integrity' ? (
                        <StatementIntegrityBadge
                          integrity={statement.integrity}
                          integrityAvailable={integrityAvailable}
                          integrityReason={integrityReason}
                          periodEndLabel={integrityPeriodEndLabel}
                          onClick={onIntegrityClick && integrityAvailable ? () => onIntegrityClick(statement) : undefined}
                        />
                      ) : column.key === 'account' ? (
                        // The name, and beside it whether a note for the
                        // customer goes on the statement (kept in view
                        // however long the name).
                        <span className="sr-name-cell">
                          <span className="sr-name-text">{formatValue(statement[column.key], column.format, statement)}</span>
                          {hasCustomerNote(statement) ? (
                            <span className="sr-badge solid warning sr-customer-note"
                              title={`Note for the customer — printed on the statement and in the email: ${String(statement.note).trim()}`}>
                              <span className="material-symbols-rounded" aria-hidden="true">{NOTE_FIELDS.note.icon}</span>
                              {NOTE_FIELDS.note.short}
                            </span>
                          ) : null}
                        </span>
                      ) : formatValue(statement[column.key], column.format, statement)}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className="actions-col">
                      {/* The buttons' row is this div's, not the cell's: a td
                          made a flex box loses its table-cell height and
                          alignment (TableStyles .actions-cell). */}
                      <div className="actions-cell">
                      {actions.map(action => {
                        const label = typeof action.label === 'function'
                          ? action.label(statement)
                          : action.label;
                        const icon = typeof action.icon === 'function'
                          ? action.icon(statement)
                          : action.icon;

                        // Disable, never hide: a button that does not apply
                        // stays in its slot and its title says why.
                        const disabled = !!action.isDisabled?.(statement);
                        const busy = !!action.isBusy?.(statement);
                        const disabledTitle = disabled ? action.disabledTitle?.(statement) : null;

                        return (
                          <button
                            key={action.type}
                            onClick={() => action.handler(statement)}
                            className={`btn-icon action-${action.type}${disabled ? ' btn-disabled' : ''}`}
                            title={disabledTitle || label}
                            aria-label={label}
                            disabled={disabled || busy}
                          >
                            <span className={`material-symbols-rounded${busy ? ' stmt-integrity-spin' : ''}`}>
                              {busy ? 'progress_activity' : icon}
                            </span>
                          </button>
                        );
                      })}
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default StatementTable;
